// Controllers
import { LoggingConstants } from "Constants";
import { ReferenceControllerSingleton } from "Controllers";
// Enums
import { ReferenceTypeEnum } from "Enums";
// Helpers
import { MarkdownItHelperSingleton, PendoHelperSingleton } from "Helpers";
// Types
import { TOpenReferencePreviewModalCustomEventDetail, TReference, TTechnologyImageDTO, TTechnologyYoutubeVideoDTO } from "Types";

class ReferenceHelper {
    private referencePreviewModal: HTMLDivElement | undefined;

    public getReferenceFromHash(hash: string): TReference | undefined {
        const hashSplit: string[] = hash.split("-");
        if (hashSplit.length < 2) return undefined;

        const urlHashType: string = hashSplit[0];
        let reference: TReference | undefined = undefined;

        switch (urlHashType) {
            case "#article":
                if (hashSplit.length >= 4) {
                    switch (hashSplit[3].toUpperCase()) {
                        case "SC":
                            PendoHelperSingleton.trackEventWithProperties(`${LoggingConstants.DYNAMIC_DELIVERY}ReferenceModalScience`, {
                                articleId: hashSplit[2]
                            });
                            reference = {
                                type: ReferenceTypeEnum.ScienceArticle,
                                referenceId: hashSplit[2]
                            };
                            break;
                        case "PA":
                            PendoHelperSingleton.trackEventWithProperties(`${LoggingConstants.DYNAMIC_DELIVERY}ReferenceModalPatent`, {
                                patentId: hashSplit[2]
                            });
                            reference = {
                                type: ReferenceTypeEnum.Patent,
                                referenceId: hashSplit[2]
                            };
                            break;
                        default:
                            break;
                    }
                }
                break;
            case "#internalLink":
                PendoHelperSingleton.trackEventWithProperties(`${LoggingConstants.DYNAMIC_DELIVERY}ReferenceModalWeblink`, {
                    weblinkId: hashSplit[1],
                    technologyId: hashSplit[2] ? hashSplit[2] : undefined
                });
                reference = {
                    type: ReferenceTypeEnum.InternalLink,
                    referenceId: hashSplit[1],
                    technologyId: hashSplit[2] ? hashSplit[2] : undefined
                };
                break;
            case "#technology":
                PendoHelperSingleton.trackEventWithProperties(`${LoggingConstants.DYNAMIC_DELIVERY}ReferenceModalTechnology`, {
                    technologyId: hashSplit[1]
                });
                reference = {
                    type: ReferenceTypeEnum.Technology,
                    referenceId: hashSplit[1]
                };
                break;
            case "#image":
                PendoHelperSingleton.trackEventWithProperties(`${LoggingConstants.DYNAMIC_DELIVERY}ReferenceModalImage`, {
                    imageId: hashSplit[1],
                    technologyId: hashSplit[2] ? hashSplit[2] : undefined
                });
                reference = {
                    type: ReferenceTypeEnum.Image,
                    referenceId: hashSplit[1],
                    technologyId: hashSplit[2] ? hashSplit[2] : undefined
                };
                break;
            case "#video":
                PendoHelperSingleton.trackEventWithProperties(`${LoggingConstants.DYNAMIC_DELIVERY}ReferenceModalYoutubeVideo`, {
                    videoId: hashSplit[1],
                    technologyId: hashSplit[2] ? hashSplit[2] : undefined
                });
                reference = {
                    type: ReferenceTypeEnum.YoutubeVideo,
                    referenceId: hashSplit[1],
                    technologyId: hashSplit[2] ? hashSplit[2] : undefined
                };
                break;
            default:
                return undefined;
        }

        return reference;
    }

    public removeHashFromURL(): void {
        const url = `${window.location.pathname} ${window.location.search}`;
        window.history.replaceState(
            {}, 
            document.title, 
            url
        );
    }
    
    public setReferencePreviewModalWidthForMedias(): void {
        // safety-checks
        if (!this.referencePreviewModal) { return; }

        // Set the width and height of the modal to the max width & height
        let maxWidth = 500;
        if (window.innerWidth * 0.4 < 500) {
            maxWidth = window.innerWidth * 0.4;
        }
        this.referencePreviewModal.style.width = `${maxWidth}px`;
    }

    public async handleOpenReferencePreviewModalCustomEventAsync(openReferencePreviewModalCustomEventDetail: TOpenReferencePreviewModalCustomEventDetail): Promise<void> {
        // get the target HTML anchor element of the openReferencePreviewModalCustomEvent custom event
        const referenceHTMLAnchorElement: HTMLAnchorElement = openReferencePreviewModalCustomEventDetail.target;
        // manage show/hide modal
        let showModal = true;
        referenceHTMLAnchorElement.onmouseleave =  () => {
            showModal = false;
            if (this.referencePreviewModal) {
                this.referencePreviewModal.style.opacity = "0";
                this.referencePreviewModal.style.pointerEvents = "none";
            }
        };

        // get the reference (type, type id & technology id)
        const reference: TReference | undefined = this.getReferenceFromHash(referenceHTMLAnchorElement.hash);
        if (!reference) return;
        
        // thanks to the reference get the reference preview modal data from the server
        let referencePreviewModalData: string | TTechnologyImageDTO | TTechnologyYoutubeVideoDTO | undefined = await ReferenceControllerSingleton.getReferencePreviewDataAsync(reference);
        if (!referencePreviewModalData) return;

        // if the reference preview modal has never been created
        if (!this.referencePreviewModal) {
            // create it, set its style and add it to the DOM
            this.referencePreviewModal = document.createElement("div");
            this.referencePreviewModal.classList.add("referencePreviewModal");
            document.body.appendChild(this.referencePreviewModal);
        }

        // Set the max width and height of the modal in contrast of the window size
        if (window.innerWidth * 0.4 > 500) {
            this.referencePreviewModal.style.maxWidth = "500px";
        } else {
            this.referencePreviewModal.style.maxWidth = `${window.innerWidth * 0.4}px`;
        }
        this.referencePreviewModal.style.maxHeight = `${window.innerHeight - 112}px`;
        // Add content to the preview modal
        if (reference.type === ReferenceTypeEnum.Image) {
            referencePreviewModalData = referencePreviewModalData as TTechnologyImageDTO;
            this.referencePreviewModal.innerHTML = `
            <div style="display: flex; flex-direction: column; align-items: center">
                <div style="overflow: hidden"><img src="${referencePreviewModalData.link}"></div>
                <div>${MarkdownItHelperSingleton.render(referencePreviewModalData.caption, reference.technologyId? {"technologyId": reference.technologyId} : undefined)}</div>
            </div>`;
            this.setReferencePreviewModalWidthForMedias();
        } else if (reference.type === ReferenceTypeEnum.YoutubeVideo) {
            const technologyYoutubeVideoData = referencePreviewModalData as TTechnologyYoutubeVideoDTO;
            this.referencePreviewModal.innerHTML = `
            <div style="display: flex; flex-direction: column; align-items: center">
                <div style="overflow: hidden"><img src="${technologyYoutubeVideoData.previewLink}"></div>
                <div>${MarkdownItHelperSingleton.render(technologyYoutubeVideoData.caption, reference.technologyId? {"technologyId": reference.technologyId} : undefined)}</div>
            </div>`;
            this.setReferencePreviewModalWidthForMedias();
        } else {
            this.referencePreviewModal.innerHTML = `<p>${referencePreviewModalData}</p>`;
            // Set the width and height on auto to change to the size of the content
            this.referencePreviewModal.style.width = "auto";
            this.referencePreviewModal.style.height = "auto";
        }
        
        // set the reference preview modal positions
        const linkElementRect = referenceHTMLAnchorElement.getBoundingClientRect();
        const popupElementRect = this.referencePreviewModal.getBoundingClientRect();
        
        // Define preview modal X position
        if (linkElementRect.right + popupElementRect.width > window.innerWidth - 24) {
            this.referencePreviewModal.style.left = `${linkElementRect.left - popupElementRect.width - 8}px`;
        } else {
            this.referencePreviewModal.style.left = `${linkElementRect.right + 8}px`;
        }
        // Define preview modal Y position
        if (linkElementRect.bottom + popupElementRect.height > window.innerHeight - 16) {
            this.referencePreviewModal.style.top = `${window.innerHeight - popupElementRect.height - 16}px`;
        } else {
            this.referencePreviewModal.style.top = `${linkElementRect.top}px`;
        }

        // Show modal
        if (showModal) {
            this.referencePreviewModal.style.opacity = "1";
            this.referencePreviewModal.style.pointerEvents = "initial";
        }
    }
}

export const ReferenceHelperSingleton = new ReferenceHelper();
